import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import { Close, FiberManualRecord, Stop } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

interface AudioRecorderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the finished recording; the dialog closes itself after. */
  onAttach: (file: File, durationSeconds: number) => void;
}

/** Pick the best container the browser supports (Safari/iPad = mp4, Chrome = webm). */
function pickMimeType(): string {
  const candidates = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return '';
}

function extFromMime(mime: string): string {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('webm')) return 'webm';
  return 'audio';
}

function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * In-app voice recorder — iPad file inputs don't offer a microphone option,
 * so audio observations are captured with MediaRecorder directly.
 */
export const AudioRecorderDialog = ({ open, onClose, onAttach }: AudioRecorderDialogProps) => {
  const theme = useTheme();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const mimeRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationRef = useRef(0);

  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    rec?.stream.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      cleanup();
      setRecording(false);
      setElapsed(0);
      setError(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      blobRef.current = null;
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startRecording = async () => {
    setError(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    blobRef.current = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      mimeRef.current = mimeType || 'audio/webm';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setElapsed(0);
      durationRef.current = 0;
      timerRef.current = setInterval(() => {
        durationRef.current += 1;
        setElapsed(durationRef.current);
      }, 1000);
    } catch {
      setError('Microphone access was denied or is unavailable on this device.');
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    setRecording(false);
  };

  const handleAttach = () => {
    const blob = blobRef.current;
    if (!blob) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const file = new File([blob], `voice-note-${stamp}.${extFromMime(mimeRef.current)}`, { type: mimeRef.current });
    onAttach(file, durationRef.current);
    onClose();
  };

  return (
    <Dialog open={open} onClose={recording ? undefined : onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700, pb: 0.5 }}>
        Record Voice Note
        <IconButton size="small" onClick={onClose} disabled={recording}><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 2 }}>
          {error ? (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>{error}</Typography>
          ) : (
            <>
              <IconButton
                onClick={recording ? stopRecording : startRecording}
                sx={{
                  width: 84, height: 84,
                  backgroundColor: recording
                    ? alpha(theme.palette.error.main, 0.12)
                    : alpha(theme.palette.primary.main, 0.1),
                  '&:hover': {
                    backgroundColor: recording
                      ? alpha(theme.palette.error.main, 0.2)
                      : alpha(theme.palette.primary.main, 0.18),
                  },
                }}
              >
                {recording
                  ? <Stop sx={{ fontSize: 40, color: theme.palette.error.main }} />
                  : <FiberManualRecord sx={{ fontSize: 40, color: theme.palette.error.main }} />}
              </IconButton>
              <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                {formatElapsed(elapsed)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {recording ? 'Recording… tap to stop' : previewUrl ? 'Review below, then attach' : 'Tap to start recording'}
              </Typography>
              {previewUrl && !recording && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <audio controls src={previewUrl} style={{ width: '100%' }} />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={recording} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleAttach} disabled={recording || !previewUrl}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Attach Recording
        </Button>
      </DialogActions>
    </Dialog>
  );
};
